import * as React from "react";
import { StandardLayout } from "../../../layouts/standard";
import PageLoader from "../../../components/common/pageLoader";
import { useParams, Link as RouterLink } from "react-router-dom";
import { SimpleBreadcrumbs } from "../../../components/navigation/simpleBreadcrumbs";
import { Typography, Link, Alert, AlertTitle, Stack } from "@mui/material";
import SessionGeneralPanel from "./general/sessionGeneralPanel";
import SessionParticipantsPanel from "./participant/sessionParticipantsPanel";
import SessionResourcesPanel from "./resources/sessionResourcesPanel";
import { useGetCclEventByEventKeyQuery } from "../../../services/cclTokenedSessionApi";
import DetailPage, { DetailPageTab } from "../../../components/common/detailPage/detailPage";
import SessionAssignmentsPanel from "./assignments/sessionAssignmentsPanel";
import SessionDigitalProductsPanel from "./digitalProducts/sessionDigitalProductsPanel";
import SessionDocumentsPanel from "./documents/sessionDocumentsPanel";
import SessionFilesPanel from "./files/sessionFilesPanel";
import SessionHistoryPanel from "./history/sessionHistoryPanel";
import CustomNotificationPanel from "./customNotification/customNotificationPanel";
import CclGenericConfirmationDialog from "../../../components/common/cclGenericConfirmationDialog";
import {
    UserClaimsService,
    eGroupIds,
    eRoleIds,
} from "../../../services/currentUserService/currentUserService";
import { getErrorMsg } from "../../../utilities/rtkQueryErrorHelpers";
import useCombinationKeyPress from "../../../hooks/useCombinationKeyPress";
import AssessmentsPanel from "./assessments/assessmentsPanel";
import RegistrationJournalDrawer from "../../registrations/registrationDetail/registrationJournalDrawer";
import { useGetCclParticipantsByEventKeyQuery } from "../../../services/cclTokenedEnterpriseParticipantApi";
import { Participant } from "../../../services/types/enterpriseParticipantApiTypes";
import { FeatureFlag } from "../../../featureFlags";
import { useGetSessionsInventoryQuery } from "../../../services/cclTokenedAssessmentApi";
import ScoringSnapshot from "../../assessments/scoringSnapshot";
import SessionPayrollTabPanel from "./payroll/sessionPayrollTabPanel";
import ScoringStatusPanel from "./assessments/scoringStatus/scoringStatusPanel";
import { AP_SURVEY_PLATFORM_KEYS } from "../../../services/types/sessionDetailTypes";

export type SessionDetailProps = {
    sessionId: string;
    tabIndex?: string;
};

export const SessionDetail: React.FC = (props) => {
    const params = useParams<SessionDetailProps>();
    const sessionId = params.sessionId !== undefined ? params.sessionId : "";
    const requestedTabIndex = params.tabIndex !== undefined ? params.tabIndex : "";

    const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
    const [saveNeeded, setSaveNeeded] = React.useState<boolean>(false);
    const [requestedTab, setRequestedTab] = React.useState<number>(0);
    const [selectedParticipants, setSelectedParticipants] = React.useState<Participant[]>([]);
    const [emailRecipients, setEmailRecipients] = React.useState<Participant[] | undefined>(
        undefined,
    );
    const [openJournal, setOpenJournal] = React.useState<boolean>(false);
    const [actualTab, setActualTab] = React.useState<number>(
        requestedTabIndex ? +requestedTabIndex : 0,
    );
    const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);
    const { data, isLoading, isSuccess, isError, error } = useGetCclEventByEventKeyQuery(
        sessionId,
        { skip: sessionId === "" },
    );
    const { data: participants } = useGetCclParticipantsByEventKeyQuery(sessionId, {
        skip: sessionId === "",
    });
    const isProductSale = data?.typeKey === 7 || data?.typeKey === 9;
    const sessionName = data?.title ?? "Unknown Event";
    const features = React.useContext(FeatureFlag);
    const shouldFetchInventory = isProductSale && data?.sessionKey != null;
    const { data: inventoryData, isFetching: inventoryIsFetching } = useGetSessionsInventoryQuery(
        data?.sessionKey!,
        {
            skip: !shouldFetchInventory,
        },
    );
    const detailEl = React.useRef<HTMLDivElement>(null);
    const onKeyDown = (event: KeyboardEvent) => {
        setDrawerOpen(true);
        event.preventDefault();
    };
    useCombinationKeyPress(["j"], onKeyDown, detailEl.current);
    const claimsService = new UserClaimsService();
    const handleSetSaveNeeded = (saveNeeded: boolean) => {
        setSaveNeeded(saveNeeded);
    };

    const allowJournalEntries =
        claimsService.IsUserCclAdminOrSuperAdmin() ||
        claimsService.IsUserNwaAdmin() ||
        claimsService.IsUserInGroups([eGroupIds.eClientAdmin]);

    const handleJournalEntry = (keys: string[]) => {
        const selPax = participants?.filter((p) => keys.includes(p.esiKey.toString()));
        setSelectedParticipants(selPax ?? []);
        setOpenJournal(true);
    };

    const isOE = data?.typeKey === 6;
    // Licensee-managed sessions don't support email customization, so the action would only lead
    // to a confusing "not available" message — hide it rather than show a dead end.
    const isLicensee = data?.managingLocationKey === 6;

    const handleSendParticipantsEmail = (esiKeys: number[]) => {
        const selected = participants?.filter((p) => esiKeys.includes(p.esiKey)) ?? [];
        setEmailRecipients(selected);
        setActualTab(2);
    };

    const isClientAdmin = claimsService.IsUserInGroups([eGroupIds.eClientAdmin]);
    const isSuperAdmin = claimsService.IsUserInGroups([eGroupIds.eCCLSuperAdmin]);
    const isPayrollManager =
        claimsService.IsUserInGroups([eGroupIds.eCCLAdmin]) &&
        claimsService.IsUserInAppRoles([eRoleIds.ePayrollManager]);
    const isCclAdmin = claimsService.IsUserCclAdminOrSuperAdmin();
    const isNwaAdmin = claimsService.IsUserNwaAdmin();
    const isAdmin = isCclAdmin || isNwaAdmin;
    const isActive = data?.status.toLowerCase() === "active";
    const isDraft = data?.status.toLowerCase() === "draft";
    const isInSetup = data?.status.toLowerCase() === "setup in progress";
    const isReadOnly = claimsService.IsReadOnly();
    const isPayrollFeature = !!features?.["payroll-admin"]?.active;
    const isApSurveyPlatform = AP_SURVEY_PLATFORM_KEYS.includes(
        data?.assessmentPlatformKey ?? 0,
    );

    const displayPayroll =  () => {
        if (!isPayrollFeature) {
            return false;
        }

        // Licensee-managed sessions (e.g. Eckerd College) shouldn't have payroll entered against
        // them, so hide the tab entirely rather than risk data being recorded incorrectly.
        if (isLicensee) {
            return false;
        }
        
        //only display for payroll managers, superadmins, and if the payroll feature is enabled and if the session type is 3, 10, or 6 (custom, coaching, OE)
        if (!(data?.typeKey === 3 || data?.typeKey === 10 || data?.typeKey === 6)) {
            return false;
        }

        if (isSuperAdmin || isPayrollManager) {
            return true;
        }

        return false;
    };

    let tablist: DetailPageTab[] = [
        {
            label: "Details",
            index: 0,
            content: <SessionGeneralPanel sessionId={sessionId} isProductSale={isProductSale} />,
            saveable: false,
            visible: true,
        },
        {
            label: "Registrations",
            index: 4,
            content: (
                <SessionParticipantsPanel
                    sessionId={sessionId}
                    inventory={
                        !inventoryIsFetching && inventoryData != null ? inventoryData : undefined
                    }
                    inventoryIsFetching={inventoryIsFetching}
                />
            ),
            saveable: true,
            visible: isActive,
        },
        {
            label: "Assessments",
            index: 10,
            content: isActive ? (
                <AssessmentsPanel
                    sessionDetails={data}
                    handleJournalEntry={handleJournalEntry}
                    showSendParticipantsEmail={isOE && isCclAdmin && !isLicensee}
                    onSendParticipantsEmail={handleSendParticipantsEmail}
                />
            ) : (
                <ScoringStatusPanel
                    handleJournalEntry={handleJournalEntry}
                    source="session"
                    sessionDetails={data}
                    sessionKey={data?.sessionKey}
                    sessionCode={data?.sessionCode}
                    hostPlatformKey={data?.hostingPlatformKey ?? 0}
                    surveyPlatformKey={data?.assessmentPlatformKey ?? 0}
                    startDate={data?.start ?? null}
                />
            ),
            saveable: true,
            visible: isApSurveyPlatform && !isDraft && !isInSetup && (isAdmin || isClientAdmin),
        },
        {
            label: "Scoring",
            index: 11,
            content: <ScoringSnapshot fromSessionDetail={true} sessionId={sessionId} />,
            saveable: false,
            visible: isApSurveyPlatform && !isDraft && !isInSetup,
        },
        {
            label: "Files",
            index: 7,
            content: (
                <SessionFilesPanel
                    sessionId={sessionId}
                    clientAdmin={isClientAdmin}
                    isProductSale={isProductSale}
                />
            ),
            saveable: false,
            visible: isActive,
        },
        {
            label: "Resources",
            index: 5,
            content: (
                <SessionResourcesPanel sessionId={sessionId} setSaveNeeded={handleSetSaveNeeded} />
            ),
            saveable: false,
            visible: isActive && !isProductSale && !isClientAdmin,
        },
        {
            label: "Assignments",
            index: 3,
            content: <SessionAssignmentsPanel sessionId={sessionId} />,
            saveable: false,
            visible: isActive && !isProductSale && !isClientAdmin && !isNwaAdmin,
        },
        {
            label: "Payroll",
            index: 12,
            content: <SessionPayrollTabPanel session={data} sessionName={sessionName} sessionId={sessionId} />,
            saveable: false,
            visible: displayPayroll(),
        },
        {
            label: "Documents",
            index: 6,
            content: <SessionDocumentsPanel sessionId={sessionId} />,
            saveable: false,
            visible: isActive && !isProductSale && !isClientAdmin && !isReadOnly,
        },
        {
            label: "Emails",
            index: 2,
            content: (
                <CustomNotificationPanel
                    sessionId={sessionId}
                    participants={emailRecipients ?? participants ?? []}
                    allParticipants={emailRecipients === undefined}
                />
            ),
            saveable: false,
            visible: isActive && !isProductSale && !isReadOnly && !isClientAdmin && !isNwaAdmin,
        },
        {
            label: "Products",
            index: 8,
            content: (
                <SessionDigitalProductsPanel sessionId={sessionId} clientAdmin={isClientAdmin} />
            ),
            saveable: false,
            visible: isActive && !isProductSale,
        },
        {
            label: "Logs",
            index: 9,
            content: <SessionHistoryPanel sessionId={sessionId} drawerOpen={drawerOpen} />,
            saveable: false,
            visible: true,
        },
    ];

    React.useEffect(() => {
        if (!isSuccess) return;
        const visibleIndices = tablist
            .filter((t) => t.visible !== false)
            .map((t) => t.index);
        if (visibleIndices.length > 0 && !visibleIndices.includes(actualTab)) {
            setActualTab(visibleIndices[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess, isApSurveyPlatform, isDraft, isInSetup, isActive, isLicensee]);

    function showErrorAlert(msg: string | undefined) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <div>
                    <Alert severity="error" sx={{ fontSize: 14 }}>
                        <AlertTitle sx={{ fontSize: 14 }}>Error</AlertTitle>
                        {msg}
                    </Alert>
                </div>
            </StandardLayout>
        );
    }

    const handleTabChangeRequest = (event: React.SyntheticEvent, newValue: number) => {
        if (!saveNeeded) {
            setActualTab(newValue);
            return;
        }
        // save needed so open dialog
        setRequestedTab(newValue);
        setConfirmOpen(true);
    };

    const onJournalEntrySaved = () => {
        setOpenJournal(false);
    };

    const breadcrumbs = (
        <Stack direction={"row"} spacing={3}>
            <SimpleBreadcrumbs>
                <Typography variant="h6" color="text.primary">
                    <Link key="findSessionLink" component={RouterLink} to="/sessions">
                        Sessions
                    </Link>
                </Typography>
                <Typography variant="h6" color="text.primary">
                    {data != null ? data.title : "Unknown Event"}
                </Typography>
            </SimpleBreadcrumbs>
        </Stack>
    );

    if (isLoading) {
        return (
            <StandardLayout breadcrumbs={breadcrumbs}>
                <PageLoader msg={""} />
            </StandardLayout>
        );
    }

    if (isError) {
        return showErrorAlert(`Error retrieving ${sessionId} (${getErrorMsg(error)})`);
    }

    if (isSuccess && data != null) {
        return (
            <div ref={detailEl}>
                <CclGenericConfirmationDialog
                    open={confirmOpen}
                    onCancel={() => setConfirmOpen(false)}
                    onOk={() => {
                        setActualTab(requestedTab);
                        setSaveNeeded(false);
                        setConfirmOpen(false);
                    }}
                    msg={"You're changes have not been saved."}
                />
                {allowJournalEntries && participants !== undefined ? (
                    <RegistrationJournalDrawer
                        registrations={selectedParticipants ?? []}
                        sessionCode={data?.sessionCode ?? ""}
                        showCountBanner={true}
                        open={openJournal}
                        onClose={() => setOpenJournal(false)}
                        onSave={onJournalEntrySaved}
                    />
                ) : null}
                <DetailPage
                    descriptor={"session"}
                    breadcrumbs={breadcrumbs}
                    tablist={tablist}
                    handleTabChange={handleTabChangeRequest}
                    activeTab={actualTab}
                    showSavePreferences={true}
                    preferencesArea="sessionDetail"
                />
            </div>
        );
    }

    return showErrorAlert(`No session data found for session ${sessionId}`);
};

export default SessionDetail;
